<template>
  <main class="model-catalog-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="搜索模型名称..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch" @clear="onSearch"
        />
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="onAdd">新增</el-button>
      </div>
      <div class="desktop-only">
        <el-table :data="models" size="small" style="width: 100%">
          <el-table-column type="index" label="序号" width="70" :index="pageIndex" />
          <el-table-column label="模型" min-width="220">
            <template slot-scope="scope">
              <div class="model-cell">
                <span class="model-icon">
                  <img v-if="scope.row.icon" :src="scope.row.icon" :alt="scope.row.name" @error="scope.row.icon = ''">
                  <span v-else>{{ modelAbbr(scope.row.name) }}</span>
                </span>
                <span class="model-name">{{ scope.row.name }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="图标状态" width="120">
            <template slot-scope="scope">
              <el-tag size="small" :type="scope.row.icon ? 'success' : 'info'">{{ scope.row.icon ? '已上传' : '未上传' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="140">
            <template slot-scope="scope">
              <el-button size="small" type="text" @click="onEdit(scope.row)">编辑</el-button>
              <el-button size="small" type="text" class="delete-btn" @click="onDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mobile-only list-cards">
        <div v-for="row in models" :key="row.id" class="list-card">
          <div class="card-header">
            <div class="model-cell" style="display:inline-flex;align-items:center;gap:8px;">
              <span class="model-icon" style="width:26px;height:26px;font-size:12px;">
                <img v-if="row.icon" :src="row.icon" :alt="row.name" @error="row.icon = ''">
                <span v-else>{{ modelAbbr(row.name) }}</span>
              </span>
              <div class="card-title">{{ row.name }}</div>
            </div>
            <el-tag size="mini" :type="row.icon ? 'success' : 'info'">{{ row.icon ? '已上传' : '未上传' }}</el-tag>
          </div>
          <div class="meta-row">
            <span>{{ $formatTime(row.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button size="small" type="text" @click="onEdit(row)">编辑</el-button>
          </div>
        </div>
      </div>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
      <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px" :close-on-click-modal="false">
        <el-form ref="form" :model="form" :rules="rules" label-width="80px" label-position="right" size="small">
          <el-form-item label="模型名称" prop="name">
            <el-input v-model="form.name" placeholder="例: deepseek-v4-flash" />
          </el-form-item>
          <el-form-item label="模型图标" prop="icon">
            <div class="upload-row">
              <input ref="iconInput" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" class="file-input" @change="onIconChange">
              <el-button size="small" plain icon="el-icon-upload2" @click="$refs.iconInput.click()">选择图片</el-button>
              <el-button v-if="form.icon" size="small" type="text" class="delete-btn" @click="removeIcon">移除图标</el-button>
              <span class="upload-tip">支持 PNG/JPG/SVG/WebP，最大 512KB</span>
            </div>
          </el-form-item>
          <div class="preview-row">
            <span class="model-icon large">
              <img v-if="form.icon" :src="form.icon" :alt="form.name" @error="form.icon = ''">
              <span v-else>{{ modelAbbr(form.name) }}</span>
            </span>
            <span>{{ form.name || '模型预览' }}</span>
          </div>
        </el-form>
        <span slot="footer">
          <el-button @click="dialogVisible = false" size="small">取消</el-button>
          <el-button type="primary" @click="submitForm" size="small" :loading="submitting">确定</el-button>
        </span>
      </el-dialog>
    </section>
  </main>
</template>

<script>
export default {
  name: 'ModelCatalogPage',
  data() {
    return {
      models: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: '',
      dialogVisible: false,
      dialogTitle: '',
      submitting: false,
      form: {
        id: null,
        name: '',
        icon: ''
      },
      rules: {
        name: [
          { required: true, message: '请输入模型名称', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    }
  },
  mounted() {
    this.fetchModels()
  },
  methods: {
    modelAbbr(name) {
      if (!name) return 'M'
      return name.substring(0, 2).toUpperCase()
    },
    async fetchModels() {
      try {
        const res = await this.$http.get('/admin/model-catalog', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.models = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载模型库失败')
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchModels()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchModels()
    },
    onAdd() {
      this.dialogTitle = '新增模型'
      this.form = { id: null, name: '', icon: '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    onEdit(row) {
      this.dialogTitle = '编辑模型'
      this.form = { id: row.id, name: row.name, icon: row.icon || '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    onIconChange(event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (file.size > 512 * 1024) {
        this.$message.warning('图标不能超过 512KB')
        return
      }
      const reader = new FileReader()
      reader.onload = e => {
        this.form.icon = e.target.result
      }
      reader.readAsDataURL(file)
    },
    removeIcon() {
      this.form.icon = ''
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            name: this.form.name,
            icon: this.form.icon
          }
          if (this.form.id) {
            await this.$http.put('/admin/model-catalog/' + this.form.id, payload)
            this.$message.success('编辑成功')
          } else {
            await this.$http.post('/admin/model-catalog', payload)
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchModels()
        } catch (e) {
          this.$message.error(e.response?.data?.error || '操作失败')
        } finally {
          this.submitting = false
        }
      })
    },
    onDelete(row) {
      this.$confirm('确定删除模型「' + row.name + '」？', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/model-catalog/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchModels()
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.model-catalog-page {
  padding: 18px 24px 76px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}

.search-input {
  width: 220px;
}

.model-cell,
.preview-row,
.upload-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-row {
  flex-wrap: wrap;
}

.file-input {
  display: none;
}

.upload-tip {
  font-size: 12px;
  color: var(--muted-2);
}

.preview-row {
  padding: 10px 14px;
  border: 1px solid var(--line-soft);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.045);
  color: var(--text);
  font-weight: 700;
}

.model-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  overflow: hidden;
  color: var(--cyan);
  font-size: 12px;
  font-weight: 800;
  background: rgba(79, 134, 255, 0.14);
  border: 1px solid rgba(133, 164, 255, 0.16);
}

.model-icon.large {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.model-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.model-name {
  font-weight: 700;
  color: var(--text);
}

.delete-btn {
  color: #ff93a4;
}

.delete-btn:hover {
  color: #ffb6c1;
}

.list-cards {
  display: grid;
  gap: 14px;
}

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .model-catalog-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
    overflow-x: visible;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
}
/* Card redesign */
.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
  overflow: hidden;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.card-sub {
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin-top: 2px;
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255,255,255,.35);
}
.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
}
.card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 10px;
  height: auto;
}
</style>
